/**
 * @file     sellerOffMarketFlow.ts
 * @readme   ../README.md
 * @purpose  Off-market seller flow (/sell-off-market): area, home type, reasons, timeline, then contact. A `seller` lead with no street address; the address is taken on the follow-up call.
 */

import type { FlowConfig } from '../types';

const K = 'sellOffMarketFlow';

/** Areas the flow can preselect from a hub link (`?city=<id>`), id to stored value. */
export const OFF_MARKET_AREAS: Record<string, string> = {
  vancouver: 'Vancouver',
  'north-vancouver': 'North Vancouver',
  coquitlam: 'Coquitlam',
  surrey: 'Surrey',
  delta: 'Delta',
};

const area = (value: string, labelDefault = value) => ({
  value,
  labelKey: `${K}.area.options.${value.toLowerCase().replace(/\s+/g, '-')}`,
  labelDefault,
});

export const SELLER_OFF_MARKET_FLOW_CONFIG: FlowConfig = {
  flowId: 'seller_off_market',
  slug: '/sell-off-market',
  leadType: 'seller',
  progressVariant: 'compact',
  steps: [
    {
      id: 'offMarketArea',
      type: 'select',
      headlineKey: `${K}.area.title`,
      headlineDefault: 'Where is your home?',
      options: [...Object.values(OFF_MARKET_AREAS).map((name) => area(name)), area('other', 'Somewhere else')],
    },
    {
      id: 'propertyType',
      type: 'select',
      headlineKey: `${K}.type.title`,
      headlineDefault: 'What kind of home?',
      options: [
        { value: 'house', labelKey: `${K}.type.house.label`, labelDefault: 'House' },
        { value: 'townhouse', labelKey: `${K}.type.townhouse.label`, labelDefault: 'Townhouse' },
        { value: 'condo', labelKey: `${K}.type.condo.label`, labelDefault: 'Condo' },
      ],
    },
    {
      id: 'reasons',
      type: 'pills',
      headlineKey: `${K}.reasons.title`,
      headlineDefault: 'Why sell quietly?',
      subtextKey: `${K}.reasons.hint`,
      subtextDefault: 'Tap all that fit.',
      ctaKey: `${K}.next`,
      ctaDefault: 'Next',
      pillGroups: [
        {
          answerKey: 'offMarketReasons',
          labelKey: `${K}.reasons.label`,
          labelDefault: 'Reasons',
          required: true,
          multi: true,
          options: [
            { value: 'privacy', labelKey: `${K}.reasons.privacy`, labelDefault: 'Privacy' },
            { value: 'fewer-showings', labelKey: `${K}.reasons.fewerShowings`, labelDefault: 'Fewer showings' },
            { value: 'tenanted', labelKey: `${K}.reasons.tenanted`, labelDefault: 'Tenants live there' },
            { value: 'test-price', labelKey: `${K}.reasons.testPrice`, labelDefault: 'Test my price first' },
            { value: 'family-estate', labelKey: `${K}.reasons.familyEstate`, labelDefault: 'Family or estate sale' },
            { value: 'not-sure', labelKey: `${K}.reasons.notSure`, labelDefault: 'Not sure yet' },
          ],
        },
      ],
    },
    {
      id: 'timeline',
      type: 'select',
      headlineKey: `${K}.timeline.title`,
      headlineDefault: 'When do you want to sell?',
      options: [
        { value: '1-3-months', labelKey: `${K}.timeline.soon`, labelDefault: 'Within 3 months' },
        { value: '3-6-months', labelKey: `${K}.timeline.mid`, labelDefault: '3 to 6 months' },
        { value: '6-plus-months', labelKey: `${K}.timeline.later`, labelDefault: '6 to 12 months' },
        { value: 'exploring', labelKey: `${K}.timeline.exploring`, labelDefault: 'Just exploring' },
      ],
    },
    {
      id: 'capture',
      type: 'capture',
      headlineKey: `${K}.capture.title`,
      headlineDefault: 'Who should get in touch?',
      captureFields: ['firstName', 'lastName', 'email', 'phone'],
      requiredCaptureFields: ['firstName', 'lastName', 'email', 'phone'],
      ctaKey: `${K}.capture.cta`,
      ctaDefault: 'Send',
      submitDirect: true,
    },
    {
      id: 'confirmation',
      type: 'confirmation',
      headlineKey: `${K}.done.title`,
      headlineDefault: 'Request sent',
      bodyKey: `${K}.done.body`,
      bodyDefault:
        'Relta Real Estate can reach out to talk about a private or public sale.',
    },
  ],
  // Same event names as the valuation flow; `flow_id` separates them in GA4.
  analytics: {
    start: 'lead_flow_started',
    stepCompleted: 'lead_flow_step_completed',
  },
  buildTags: (answers) => ['Off-Market Seller', ...(answers.offMarketArea && answers.offMarketArea !== 'other' ? [`Area: ${answers.offMarketArea}`] : [])],
};
